import React, { useState, useMemo, useEffect } from 'react';
import { useWorkflowApi, ApiWorkflow } from '../hooks/useWorkflowApi';
import { useAuth } from '../contexts/AuthContext';
import { isAtLeast } from '../utils/roles';
import { KeyAreaFilterBar } from '../components/workflows/KeyAreaFilterBar';
import { CategoryAccordion } from '../components/workflows/CategoryAccordion';
import { WorkflowsEmptyState } from '../components/workflows/WorkflowsEmptyState';
import { WorkflowStepsMatrix } from '../components/workflows/WorkflowStepsMatrix';

// Status cycle: draft -> active -> completed -> draft
function getNextStatus(current: string): string {
  switch (current) {
    case 'draft':
      return 'active';
    case 'active':
      return 'completed';
    case 'completed':
      return 'draft';
    default:
      return 'draft';
  }
}

export function WorkflowsAccordionPage() {
  // 7.1: Call useWorkflowApi() for data
  const api = useWorkflowApi();
  const { workflows, categories, keyAreas, loading } = api;
  const { user } = useAuth();
  const canManage = isAtLeast(user?.role || '', 'Organizer');

  // 7.2: Manage local state
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [keyAreaFilter, setKeyAreaFilter] = useState<string | null>(null);
  const [selectedWorkflow, setSelectedWorkflow] = useState<ApiWorkflow | null>(null);
  const [showJourneyView, setShowJourneyView] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showKeyAreasManager, setShowKeyAreasManager] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards');
  const [matrixWorkflowId, setMatrixWorkflowId] = useState<string | null>(null);

  // Initialize expandedCategories with all category IDs once categories load
  useEffect(() => {
    if (categories.length > 0 && expandedCategories.size === 0) {
      setExpandedCategories(new Set(categories.map((c) => c.id)));
    }
  }, [categories]);

  // Keep selectedWorkflow in sync with latest API data
  useEffect(() => {
    if (selectedWorkflow) {
      const updated = workflows.find(w => w.id === selectedWorkflow.id);
      if (updated) {
        setSelectedWorkflow(updated);
      }
    }
  }, [workflows]);

  // 7.7: Group workflows by categoryId and filter by selected key area
  const filteredWorkflowsByCategory = useMemo(() => {
    const filtered = keyAreaFilter
      ? workflows.filter((wf) =>
          wf.keyAreas.some((ka) => ka.keyAreaId === keyAreaFilter)
        )
      : workflows;

    const grouped = new Map<string, ApiWorkflow[]>();
    for (const wf of filtered) {
      const existing = grouped.get(wf.categoryId) || [];
      existing.push(wf);
      grouped.set(wf.categoryId, existing);
    }
    return grouped;
  }, [workflows, keyAreaFilter]);

  // 7.8: Only show categories that have workflows after filtering
  const activeCategories = useMemo(() => {
    return categories.filter(
      (cat) => cat.active && (filteredWorkflowsByCategory.get(cat.id)?.length ?? 0) > 0
    );
  }, [categories, filteredWorkflowsByCategory]);

  // Flat list of filtered workflows for table view
  const filteredWorkflows = useMemo(() => {
    return keyAreaFilter
      ? workflows.filter((wf) => wf.keyAreas.some((ka) => ka.keyAreaId === keyAreaFilter))
      : workflows;
  }, [workflows, keyAreaFilter]);

  // Toggle expand/collapse for a category
  const handleToggleCategory = (categoryId: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  // 7.9: Action callbacks
  const handleView = (workflow: ApiWorkflow) => {
    setSelectedWorkflow(workflow);
    setEditMode(false);
  };

  const handleEdit = (workflow: ApiWorkflow) => {
    setSelectedWorkflow(workflow);
    setEditMode(true);
  };

  const handleJourneyMap = (workflow: ApiWorkflow) => {
    setSelectedWorkflow(workflow);
    setShowJourneyView(true);
    setEditMode(false);
  };

  const handleToggleStatus = async (workflow: ApiWorkflow) => {
    const nextStatus = getNextStatus(workflow.status);
    try {
      await api.updateWorkflow(workflow.id, { status: nextStatus });
    } catch (err) {
      alert('Failed to update workflow status. Please try again.');
    }
  };

  const handleDelete = async (workflow: ApiWorkflow) => {
    try {
      await api.deleteWorkflow(workflow.id);
    } catch (err) {
      alert('Failed to delete workflow. Please try again.');
    }
  };

  const handleCreate = async (data: { name: string; categoryId: string; icon: string }) => {
    try {
      const cat = categories.find(c => c.id === data.categoryId);
      await api.createWorkflow({
        name: data.name,
        categoryId: data.categoryId,
        icon: data.icon,
        color: cat?.color || '#1a6b3c',
      });
      setShowCreateModal(false);
    } catch (err) {
      alert('Failed to create workflow. Please try again.');
    }
  };

  // 7.3: Loading state
  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.loadingContainer}>
          <div style={styles.spinner} />
          <p style={styles.loadingText}>Loading workflows...</p>
        </div>
      </div>
    );
  }

  // 7.4: Error state — loading is false and data is empty (error condition)
  if (!loading && workflows.length === 0 && categories.length === 0 && keyAreas.length === 0) {
    return (
      <div style={styles.page}>
        <div style={styles.header}>
          <div>
            <h1 style={styles.title}>Processes & Flows</h1>
            <p style={styles.subtitle}>Manage your organization's workflows and processes</p>
          </div>
        </div>
        <WorkflowsEmptyState type="error" onRetry={() => api.refresh()} />
      </div>
    );
  }

  // 7.5: Global empty state — categories exist but no workflows
  if (!loading && workflows.length === 0 && categories.length > 0) {
    return (
      <div style={styles.page}>
        <div style={styles.header}>
          <div>
            <h1 style={styles.title}>Processes & Flows</h1>
            <p style={styles.subtitle}>Manage your organization's workflows and processes</p>
          </div>
          <button style={styles.createButton} onClick={() => setShowCreateModal(true)}>
            + Create Workflow
          </button>
        </div>
        <WorkflowsEmptyState type="empty" />
        {showCreateModal && (
          <CreateWorkflowModal
            categories={categories}
            onClose={() => setShowCreateModal(false)}
            onCreate={handleCreate}
          />
        )}
      </div>
    );
  }

  // ── Detail View (ported from GuestExperiencePage) ─────────────────────────
  if (selectedWorkflow) {
    const completedSteps = selectedWorkflow.steps.filter(s => s.status === 'completed').length;
    const totalSteps = selectedWorkflow.steps.length;
    const pct = totalSteps === 0 ? 0 : Math.round((completedSteps / totalSteps) * 100);
    const category = categories.find(c => c.id === selectedWorkflow.categoryId);

    const handleAddStep = async () => {
      try {
        await api.addStep(selectedWorkflow.id, { title: 'New Step', description: 'Describe this step', assignee: 'Unassigned' });
      } catch {
        alert('Failed to add step. Please try again.');
      }
    };

    const handleUpdateStep = async (stepId: string, field: string, value: string) => {
      try {
        await api.updateStep(stepId, { [field]: value });
      } catch {
        alert('Failed to update step. Please try again.');
      }
    };

    const handleDeleteStep = async (stepId: string) => {
      if (selectedWorkflow.steps.length <= 1) return;
      try {
        await api.deleteStep(stepId);
      } catch {
        alert('Failed to delete step. Please try again.');
      }
    };

    return (
      <div style={styles.page}>
        <button
          style={styles.backButton}
          onClick={() => { setSelectedWorkflow(null); setShowJourneyView(false); }}
          type="button"
        >
          ← Back to Workflows
        </button>

        {/* Workflow Edit Form */}
        {editMode ? (
          <WorkflowEditForm
            workflow={selectedWorkflow}
            categories={categories}
            keyAreas={keyAreas}
            onSave={async (updates) => {
              await api.updateWorkflow(selectedWorkflow.id, updates);
            }}
          />
        ) : (
          <div style={styles.detailHeader}>
            <div style={{ ...styles.detailIcon, backgroundColor: selectedWorkflow.color + '22', borderColor: selectedWorkflow.color }}>
              {selectedWorkflow.icon}
            </div>
            <div style={{ flex: 1 }}>
              <h2 style={styles.detailTitle}>{selectedWorkflow.name}</h2>
              <p style={styles.detailMeta}>
                {category?.name || 'Uncategorized'} · {totalSteps} steps · {pct}% complete
              </p>
              {selectedWorkflow.keyAreas.length > 0 && (
                <div style={{ display: 'flex', gap: '6px', marginTop: '8px', flexWrap: 'wrap' }}>
                  {selectedWorkflow.keyAreas.map(ka => (
                    <span key={ka.keyAreaId} style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '10px', backgroundColor: '#e8f5e9', color: '#1a6b3c', fontWeight: 500 }}>
                      {ka.keyArea.icon} {ka.keyArea.name}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <span style={{
              ...styles.statusBadge,
              backgroundColor: selectedWorkflow.status === 'active' ? '#1a6b3c' : selectedWorkflow.status === 'completed' ? '#c9a84c' : '#6b7280'
            }}>
              {selectedWorkflow.status}
            </span>
            {canManage && (
              <button
                style={{ ...styles.viewToggleBtn, marginLeft: '8px' }}
                onClick={() => setEditMode(true)}
                type="button"
              >
                ✏️ Edit
              </button>
            )}
          </div>
        )}

        {/* View Toggle: Steps vs Journey */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
          <button
            style={{
              ...styles.viewToggleBtn,
              ...(!showJourneyView ? { backgroundColor: '#1a6b3c', color: '#fff', borderColor: '#1a6b3c' } : {})
            }}
            onClick={() => setShowJourneyView(false)}
            type="button"
          >
            📋 Steps Editor
          </button>
          <button
            style={{
              ...styles.viewToggleBtn,
              ...(showJourneyView ? { backgroundColor: '#1a6b3c', color: '#fff', borderColor: '#1a6b3c' } : {})
            }}
            onClick={() => setShowJourneyView(true)}
            type="button"
          >
            🗺️ Journey Map
          </button>
        </div>

        {/* Journey Map View */}
        {showJourneyView && (
          <div style={styles.journeyContainer}>
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <h3 style={{ color: '#0f2d1f', fontSize: '16px', fontWeight: 600, margin: 0 }}>
                {selectedWorkflow.name} — Journey Map
              </h3>
              <p style={{ color: '#666', fontSize: '12px', marginTop: '4px' }}>
                Visual overview of workflow progress
              </p>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-start', position: 'relative', minWidth: totalSteps * 180 + 'px', padding: '80px 0 20px', overflowX: 'auto' }}>
              {/* Timeline line — positioned in the middle */}
              <div style={{ position: 'absolute', top: '100px', left: '40px', right: '40px', height: '3px', background: 'linear-gradient(90deg, #1a6b3c, #c9a84c, #1a6b3c)', borderRadius: '2px' }} />
              {selectedWorkflow.steps.map((step, idx) => {
                const dotColor = step.status === 'completed' ? '#c9a84c' : step.status === 'active' ? '#1a6b3c' : '#999';
                const dotBg = step.status === 'completed' ? '#c9a84c' : '#fff';
                return (
                  <div key={step.id} style={{ flex: 1, minWidth: '160px', display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative' }}>
                    {/* Icon — clearly above the line */}
                    <div style={{ fontSize: '28px', marginBottom: '8px', zIndex: 3 }}>{step.icon || '⚙️'}</div>
                    <div style={{ fontSize: '10px', color: '#888', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '12px', textAlign: 'center' }}>
                      Step {idx + 1}
                    </div>
                    {/* Dot — sits on the line */}
                    <div style={{
                      width: '32px', height: '32px', borderRadius: '50%',
                      border: '3px solid ' + dotColor, backgroundColor: dotBg,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '12px', fontWeight: 700,
                      color: step.status === 'completed' ? '#fff' : dotColor,
                      zIndex: 2, marginBottom: '14px'
                    }}>
                      {step.status === 'completed' ? '✓' : idx + 1}
                    </div>
                    <div style={{ textAlign: 'center', padding: '0 8px' }}>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: '#0f2d1f', marginBottom: '4px' }}>{step.title}</div>
                      <div style={{ fontSize: '11px', color: '#666', marginBottom: '6px', lineHeight: '1.3' }}>
                        {step.description.slice(0, 60)}{step.description.length > 60 ? '...' : ''}
                      </div>
                      <div style={{ fontSize: '10px', color: '#888' }}>👤 {step.assignee}</div>
                      <div style={{ marginTop: '6px' }}>
                        <span style={{
                          fontSize: '10px', padding: '2px 8px', borderRadius: '8px',
                          backgroundColor: step.status === 'completed' ? 'rgba(201,168,76,0.15)' : step.status === 'active' ? 'rgba(26,107,60,0.1)' : 'rgba(0,0,0,0.05)',
                          color: dotColor
                        }}>
                          {step.status}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{ textAlign: 'center', marginTop: '16px', fontSize: '12px', color: '#888' }}>
              {completedSteps}/{totalSteps} steps completed · {pct}% progress
            </div>
          </div>
        )}

        {/* Steps Editor View */}
        {!showJourneyView && (
          <>
            {/* Progress Bar */}
            <div style={styles.progressBar}>
              <div style={{ ...styles.progressFill, width: pct + '%' }} />
            </div>

            {/* Steps Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '24px 0 16px' }}>
              <h3 style={styles.sectionLabel}>Workflow Steps</h3>
              <button style={styles.addStepBtn} onClick={handleAddStep} type="button">+ Add Step</button>
            </div>

            {/* Steps List */}
            <div style={{ position: 'relative' }}>
              {selectedWorkflow.steps.map((step, idx) => (
                <StepCardEditor
                  key={step.id}
                  step={step}
                  index={idx}
                  totalSteps={selectedWorkflow.steps.length}
                  onSave={handleUpdateStep}
                  onDelete={() => handleDeleteStep(step.id)}
                />
              ))}
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div style={styles.page}>
      {/* 7.10: Page header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Processes & Flows</h1>
          <p style={styles.subtitle}>Manage your organization's workflows and processes</p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <a
            href="/gx-dashboard/e1000000-0000-0000-0000-000000000003"
            style={{ ...styles.createButton, backgroundColor: '#1a6b3c', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px' }}
          >
            📡 GX Dashboard
          </a>
          <button
            style={{ ...styles.createButton, backgroundColor: showKeyAreasManager ? '#555' : '#6b7280' }}
            onClick={() => setShowKeyAreasManager(!showKeyAreasManager)}
          >
            {showKeyAreasManager ? '← Back' : '⚙️ Key Areas'}
          </button>
          <button style={styles.createButton} onClick={() => setShowCreateModal(true)}>
            + Create Workflow
          </button>
        </div>
      </div>

      {/* Key Areas Manager */}
      {showKeyAreasManager && (
        <KeyAreasManager keyAreas={keyAreas} api={api} />
      )}

      {/* 7.6: KeyAreaFilterBar */}
      <KeyAreaFilterBar
        keyAreas={keyAreas}
        selected={keyAreaFilter}
        onSelect={setKeyAreaFilter}
      />

      {/* View Toggle: Grid / Table */}
      <div style={styles.viewToggleRow}>
        <button
          style={viewMode === 'cards' ? styles.viewToggleActive : styles.viewToggleInactive}
          onClick={() => setViewMode('cards')}
          aria-pressed={viewMode === 'cards'}
          type="button"
        >
          ▦ Grid
        </button>
        <button
          style={viewMode === 'table' ? styles.viewToggleActive : styles.viewToggleInactive}
          onClick={() => setViewMode('table')}
          aria-pressed={viewMode === 'table'}
          type="button"
        >
          ☰ Table
        </button>
      </div>

      {/* Matrix View */}
      {matrixWorkflowId && (() => {
        const mwf = workflows.find(w => w.id === matrixWorkflowId);
        return mwf ? (
          <WorkflowStepsMatrix
            workflowId={mwf.id}
            workflowName={mwf.name}
            canEdit={canManage}
            onBack={() => setMatrixWorkflowId(null)}
          />
        ) : null;
      })()}

      {/* Cards view (accordions) */}
      {!matrixWorkflowId && viewMode === 'cards' && (
        <div style={styles.accordionList}>
          {activeCategories.map((category) => (
            <CategoryAccordion
              key={category.id}
              category={category}
              workflows={filteredWorkflowsByCategory.get(category.id) || []}
              expanded={expandedCategories.has(category.id)}
              onToggle={() => handleToggleCategory(category.id)}
              onViewWorkflow={handleView}
              onEditWorkflow={handleEdit}
              onJourneyMap={handleJourneyMap}
              onToggleStatus={handleToggleStatus}
              onDeleteWorkflow={handleDelete}
              canEdit={canManage}
            />
          ))}
        </div>
      )}

      {/* Table view */}
      {!matrixWorkflowId && viewMode === 'table' && (
        <div style={styles.tableContainer}>
          {filteredWorkflows.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#888', padding: '24px', fontStyle: 'italic' }}>
              No workflows match your filters
            </p>
          ) : (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Workflow</th>
                  <th style={styles.th}>Category</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Progress</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredWorkflows.map((wf) => {
                  const cat = categories.find(c => c.id === wf.categoryId);
                  const steps = wf.steps || [];
                  const completed = steps.filter((s: { status: string }) => s.status === 'completed').length;
                  const total = steps.length;
                  const pct = total === 0 ? 0 : Math.round((completed / total) * 100);
                  const badgeBg = wf.status === 'active' ? '#1a6b3c' : wf.status === 'completed' ? '#c9a84c' : '#6b7280';
                  return (
                    <tr key={wf.id}>
                      <td style={styles.td}>
                        <span style={{ marginRight: '8px', fontSize: '16px' }}>{wf.icon}</span>
                        <span style={{ fontWeight: 600, color: '#0f2d1f', fontSize: '13px' }}>{wf.name}</span>
                      </td>
                      <td style={styles.td}>
                        <span style={{ fontSize: '13px', color: '#555' }}>{cat ? `${cat.icon} ${cat.name}` : '—'}</span>
                      </td>
                      <td style={styles.td}>
                        <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: '12px', backgroundColor: badgeBg, color: '#fff', fontSize: '11px', fontWeight: 600, textTransform: 'capitalize' as const }}>
                          {wf.status}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: '100px' }}>
                          <div style={{ flex: 1, height: '8px', backgroundColor: '#e5e7eb', borderRadius: '4px' }}>
                            <div style={{ height: '100%', width: pct + '%', background: 'linear-gradient(90deg, #1a6b3c, #c9a84c)', borderRadius: '4px', transition: 'width 0.3s' }} />
                          </div>
                          <span style={{ fontSize: '12px', color: '#555', fontWeight: 500, whiteSpace: 'nowrap' }}>{completed}/{total}</span>
                        </div>
                      </td>
                      <td style={styles.td}>
                        <div style={{ display: 'flex', gap: '4px' }}>
                          <button style={styles.tblBtn} onClick={() => handleView(wf)} title="View" aria-label={`View ${wf.name}`}>👁️</button>
                          <button style={styles.tblBtn} onClick={() => setMatrixWorkflowId(wf.id)} title="Matrix" aria-label={`Matrix for ${wf.name}`}>📊</button>
                          {canManage && <button style={styles.tblBtn} onClick={() => handleEdit(wf)} title="Edit" aria-label={`Edit ${wf.name}`}>✏️</button>}
                          {canManage && <button style={styles.tblBtn} onClick={() => handleDelete(wf)} title="Delete" aria-label={`Delete ${wf.name}`}>🗑️</button>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {showCreateModal && (
        <CreateWorkflowModal
          categories={categories}
          onClose={() => setShowCreateModal(false)}
          onCreate={handleCreate}
        />
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: {
    padding: '24px',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  header: {
    marginBottom: '24px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  title: {
    fontSize: '28px',
    fontWeight: 700,
    color: '#0f2d1f',
    margin: '0 0 4px 0',
  },
  subtitle: {
    fontSize: '15px',
    color: '#666',
    margin: 0,
  },
  accordionList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0px',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '80px 24px',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '4px solid #e0e0e0',
    borderTopColor: '#1a6b3c',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  loadingText: {
    marginTop: '16px',
    fontSize: '15px',
    color: '#666',
  },
  backButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '8px 16px',
    fontSize: '14px',
    fontWeight: 500,
    color: '#1a6b3c',
    backgroundColor: 'transparent',
    border: '1px solid #1a6b3c',
    borderRadius: '6px',
    cursor: 'pointer',
    marginBottom: '16px',
  },
  // Detail view styles
  detailHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '24px',
  },
  detailIcon: {
    width: '52px',
    height: '52px',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '26px',
    border: '2px solid',
  },
  detailTitle: {
    fontSize: '22px',
    fontWeight: 700,
    color: '#0f2d1f',
    margin: 0,
  },
  detailMeta: {
    fontSize: '13px',
    color: '#666',
    marginTop: '4px',
  },
  statusBadge: {
    display: 'inline-block',
    padding: '3px 10px',
    borderRadius: '12px',
    fontSize: '11px',
    fontWeight: 600,
    color: '#fff',
    textTransform: 'capitalize' as const,
  },
  viewToggleBtn: {
    padding: '8px 14px',
    fontSize: '12px',
    fontWeight: 500,
    backgroundColor: '#f5f5f5',
    color: '#333',
    border: '1px solid #e0e0e0',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  journeyContainer: {
    backgroundColor: '#f9fafb',
    border: '1px solid #e0e0e0',
    borderRadius: '12px',
    padding: '32px 24px',
    marginBottom: '24px',
    overflowX: 'auto',
  },
  progressBar: {
    height: '10px',
    backgroundColor: '#e0e0e0',
    borderRadius: '5px',
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: '5px',
    background: 'linear-gradient(90deg, #1a6b3c, #c9a84c)',
    transition: 'width 0.3s',
  },
  sectionLabel: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#0f2d1f',
    margin: 0,
  },
  addStepBtn: {
    padding: '10px 20px',
    backgroundColor: '#1a6b3c',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  stepCard: {
    display: 'flex',
    gap: '16px',
    alignItems: 'flex-start',
    padding: '16px 20px',
    backgroundColor: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '10px',
    marginBottom: '12px',
    position: 'relative' as const,
  },
  stepNumber: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    backgroundColor: '#1a6b3c',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    fontWeight: 700,
    flexShrink: 0,
  },
  stepTitleInput: {
    width: '100%',
    fontSize: '15px',
    fontWeight: 600,
    color: '#0f2d1f',
    border: 'none',
    borderBottom: '1px solid #e0e0e0',
    padding: '4px 0',
    marginBottom: '6px',
    outline: 'none',
    backgroundColor: 'transparent',
  },
  stepDescInput: {
    width: '100%',
    fontSize: '13px',
    color: '#555',
    border: '1px solid #e0e0e0',
    borderRadius: '4px',
    padding: '6px 8px',
    resize: 'vertical' as const,
    fontFamily: 'inherit',
  },
  stepLabel: {
    fontSize: '12px',
    color: '#888',
    fontWeight: 500,
  },
  stepAssigneeInput: {
    fontSize: '13px',
    border: '1px solid #e0e0e0',
    borderRadius: '4px',
    padding: '4px 8px',
    width: '150px',
  },
  stepSelect: {
    fontSize: '13px',
    border: '1px solid #e0e0e0',
    borderRadius: '4px',
    padding: '4px 8px',
    backgroundColor: '#fff',
  },
  deleteStepBtn: {
    position: 'absolute' as const,
    top: '8px',
    right: '8px',
    background: 'none',
    border: 'none',
    color: '#ccc',
    fontSize: '16px',
    cursor: 'pointer',
  },
  connector: {
    position: 'absolute' as const,
    left: '35px',
    bottom: '-12px',
    width: '2px',
    height: '12px',
    backgroundColor: '#c9a84c',
  },
  createButton: {
    padding: '10px 20px',
    backgroundColor: '#1a6b3c',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
  },
};

// ── Step Card Editor (explicit save per step) ───────────────────────────────

interface StepCardEditorProps {
  step: { id: string; title: string; description: string; assignee: string; icon: string; status: string };
  index: number;
  totalSteps: number;
  onSave: (stepId: string, field: string, value: string) => Promise<void>;
  onDelete: () => void;
}

const STEP_ICONS = [
  '⚙️', '📝', '📋', '✅', '📞', '✉️', '💬', '🔔',
  '👤', '👥', '🤝', '🏁', '🔑', '💡', '📦', '🚀',
  '💰', '🛒', '📊', '🔍', '⏰', '📅', '🎯', '🏆',
  '🎫', '🎟️', '🏷️', '🧾', '💳', '🪪', '📇', '🎪',
];

function StepCardEditor({ step, index, totalSteps, onSave, onDelete }: StepCardEditorProps) {
  const [title, setTitle] = useState(step.title);
  const [description, setDescription] = useState(step.description);
  const [assignee, setAssignee] = useState(step.assignee);
  const [icon, setIcon] = useState(step.icon || '⚙️');
  const [status, setStatus] = useState(step.status);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showIcons, setShowIcons] = useState(false);

  useEffect(() => {
    setTitle(step.title);
    setDescription(step.description);
    setAssignee(step.assignee);
    setIcon(step.icon || '⚙️');
    setStatus(step.status);
  }, [step]);

  const hasChanges = title !== step.title || description !== step.description || assignee !== step.assignee || icon !== (step.icon || '⚙️') || status !== step.status;

  const handleSaveStep = async () => {
    setSaving(true);
    try {
      if (title !== step.title) await onSave(step.id, 'title', title);
      if (description !== step.description) await onSave(step.id, 'description', description);
      if (assignee !== step.assignee) await onSave(step.id, 'assignee', assignee);
      if (icon !== (step.icon || '⚙️')) await onSave(step.id, 'icon', icon);
      if (status !== step.status) await onSave(step.id, 'status', status);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {
      alert('Failed to save step.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={stepEditorStyles.card}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
        <button
          type="button"
          onClick={() => setShowIcons(!showIcons)}
          style={{ fontSize: '20px', background: 'none', border: '1px solid #e0e0e0', borderRadius: '8px', width: '36px', height: '36px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          title="Change step icon"
        >
          {icon}
        </button>
        <div style={stepEditorStyles.number}>{index + 1}</div>
      </div>
      <div style={{ flex: 1 }}>
        {showIcons && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '8px', padding: '6px', border: '1px solid #e0e0e0', borderRadius: '6px', backgroundColor: '#fafafa' }}>
            {STEP_ICONS.map(emoji => (
              <button
                key={emoji}
                type="button"
                onClick={() => { setIcon(emoji); setShowIcons(false); }}
                style={{
                  width: '30px', height: '30px', fontSize: '14px',
                  border: icon === emoji ? '2px solid #1a6b3c' : '1px solid #e0e0e0',
                  borderRadius: '4px', backgroundColor: icon === emoji ? '#e8f5e9' : '#fff',
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                {emoji}
              </button>
            ))}
          </div>
        )}
        <input
          style={stepEditorStyles.titleInput}
          value={title}
          onChange={e => setTitle(e.target.value)}
          placeholder="Step title"
        />
        <textarea
          style={stepEditorStyles.descInput}
          value={description}
          onChange={e => setDescription(e.target.value)}
          rows={2}
          placeholder="Description"
        />
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginTop: '8px', flexWrap: 'wrap' }}>
          <label style={stepEditorStyles.label}>Assignee:</label>
          <input
            style={stepEditorStyles.assigneeInput}
            value={assignee}
            onChange={e => setAssignee(e.target.value)}
          />
          <label style={stepEditorStyles.label}>Status:</label>
          <select style={stepEditorStyles.select} value={status} onChange={e => setStatus(e.target.value)}>
            <option value="pending">Pending</option>
            <option value="active">Active</option>
            <option value="completed">Completed</option>
          </select>
          <button
            style={{
              ...stepEditorStyles.saveBtn,
              opacity: (!hasChanges || saving) ? 0.4 : 1,
              cursor: (!hasChanges || saving) ? 'not-allowed' : 'pointer',
            }}
            onClick={handleSaveStep}
            disabled={!hasChanges || saving}
            type="button"
          >
            {saving ? '...' : saved ? '✓' : 'Save'}
          </button>
          {hasChanges && <span style={{ fontSize: '10px', color: '#f59e0b' }}>•</span>}
        </div>
      </div>
      {totalSteps > 1 && (
        <button style={stepEditorStyles.deleteBtn} onClick={onDelete} type="button" title="Delete step">✕</button>
      )}
    </div>
  );
}

const stepEditorStyles: Record<string, React.CSSProperties> = {
  card: {
    display: 'flex', gap: '16px', alignItems: 'flex-start',
    padding: '16px 20px', backgroundColor: '#fff', border: '1px solid #e0e0e0',
    borderRadius: '10px', marginBottom: '12px', position: 'relative',
  },
  number: {
    width: '32px', height: '32px', borderRadius: '50%', backgroundColor: '#1a6b3c',
    color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '14px', fontWeight: 700, flexShrink: 0,
  },
  titleInput: {
    width: '100%', fontSize: '15px', fontWeight: 600, color: '#0f2d1f',
    border: 'none', borderBottom: '1px solid #e0e0e0', padding: '4px 0',
    marginBottom: '6px', outline: 'none', backgroundColor: 'transparent',
  },
  descInput: {
    width: '100%', fontSize: '13px', color: '#555', border: '1px solid #e0e0e0',
    borderRadius: '4px', padding: '6px 8px', resize: 'vertical' as const, fontFamily: 'inherit',
  },
  label: { fontSize: '12px', color: '#888', fontWeight: 500 },
  assigneeInput: { fontSize: '13px', border: '1px solid #e0e0e0', borderRadius: '4px', padding: '4px 8px', width: '150px' },
  select: { fontSize: '13px', border: '1px solid #e0e0e0', borderRadius: '4px', padding: '4px 8px', backgroundColor: '#fff' },
  saveBtn: {
    padding: '4px 12px', fontSize: '11px', fontWeight: 600,
    backgroundColor: '#1a6b3c', color: '#fff', border: 'none',
    borderRadius: '4px', cursor: 'pointer',
  },
  deleteBtn: {
    position: 'absolute' as const, top: '8px', right: '8px',
    background: 'none', border: 'none', color: '#ccc', fontSize: '16px', cursor: 'pointer',
  },
};

// ── Key Areas Manager ────────────────────────────────────────────────────────

interface KeyAreasManagerProps {
  keyAreas: { id: string; name: string; icon: string; description: string; active: boolean }[];
  api: ReturnType<typeof useWorkflowApi>;
}

function KeyAreasManager({ keyAreas, api }: KeyAreasManagerProps) {
  const [newName, setNewName] = useState('');
  const [newIcon, setNewIcon] = useState('📋');
  const [newDesc, setNewDesc] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editIcon, setEditIcon] = useState('');
  const [editDesc, setEditDesc] = useState('');

  const handleAdd = async () => {
    if (!newName.trim()) return;
    await api.createKeyArea({ name: newName.trim(), icon: newIcon, description: newDesc });
    setNewName('');
    setNewIcon('📋');
    setNewDesc('');
  };

  const handleStartEdit = (area: { id: string; name: string; icon: string; description: string }) => {
    setEditingId(area.id);
    setEditName(area.name);
    setEditIcon(area.icon);
    setEditDesc(area.description);
  };

  const handleSaveEdit = async (id: string) => {
    await api.updateKeyArea(id, { name: editName, icon: editIcon, description: editDesc });
    setEditingId(null);
  };

  const handleDelete = async (id: string) => {
    if (confirm('Delete this key area?')) {
      await api.deleteKeyArea(id);
    }
  };

  const handleToggleActive = async (id: string, currentActive: boolean) => {
    await api.updateKeyArea(id, { active: !currentActive });
  };

  return (
    <div style={kaStyles.container}>
      <h3 style={kaStyles.title}>⚙️ Manage Key Areas</h3>
      <p style={kaStyles.subtitle}>Key areas represent major events or activities. Workflows can be tagged with key areas for filtering.</p>

      {/* Existing Key Areas */}
      <div style={kaStyles.list}>
        {keyAreas.map(area => (
          <div key={area.id} style={{ ...kaStyles.item, opacity: area.active ? 1 : 0.5 }}>
            {editingId === area.id ? (
              <div style={kaStyles.editRow}>
                <input style={kaStyles.iconInput} value={editIcon} onChange={e => setEditIcon(e.target.value)} />
                <input style={kaStyles.nameInput} value={editName} onChange={e => setEditName(e.target.value)} placeholder="Name" />
                <input style={{ ...kaStyles.nameInput, flex: 2 }} value={editDesc} onChange={e => setEditDesc(e.target.value)} placeholder="Description" />
                <button style={kaStyles.saveBtn} onClick={() => handleSaveEdit(area.id)}>Save</button>
                <button style={kaStyles.cancelBtn} onClick={() => setEditingId(null)}>Cancel</button>
              </div>
            ) : (
              <div style={kaStyles.viewRow}>
                <span style={kaStyles.icon}>{area.icon}</span>
                <span style={kaStyles.name}>{area.name}</span>
                <span style={kaStyles.desc}>{area.description}</span>
                <button style={kaStyles.editBtn} onClick={() => handleStartEdit(area)}>✏️</button>
                <button style={kaStyles.toggleBtn} onClick={() => handleToggleActive(area.id, area.active)}>
                  {area.active ? '🟢' : '⚪'}
                </button>
                <button style={kaStyles.deleteBtn} onClick={() => handleDelete(area.id)}>🗑️</button>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Add New */}
      <div style={kaStyles.addSection}>
        <h4 style={kaStyles.addTitle}>+ Add New Key Area</h4>
        <div style={kaStyles.addRow}>
          <input style={kaStyles.iconInput} value={newIcon} onChange={e => setNewIcon(e.target.value)} placeholder="Icon" />
          <input style={kaStyles.nameInput} value={newName} onChange={e => setNewName(e.target.value)} placeholder="Name (e.g., Farewell Dinner)" />
          <input style={{ ...kaStyles.nameInput, flex: 2 }} value={newDesc} onChange={e => setNewDesc(e.target.value)} placeholder="Description" />
          <button style={kaStyles.saveBtn} onClick={handleAdd} disabled={!newName.trim()}>Add</button>
        </div>
      </div>
    </div>
  );
}

const kaStyles: Record<string, React.CSSProperties> = {
  container: {
    backgroundColor: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '12px',
    padding: '24px',
    marginBottom: '24px',
  },
  title: { fontSize: '16px', fontWeight: 700, color: '#0f2d1f', margin: '0 0 4px' },
  subtitle: { fontSize: '13px', color: '#666', margin: '0 0 20px' },
  list: { display: 'flex', flexDirection: 'column' as const, gap: '8px', marginBottom: '20px' },
  item: { padding: '10px 14px', borderRadius: '8px', border: '1px solid #e0e0e0', backgroundColor: '#fafafa' },
  viewRow: { display: 'flex', alignItems: 'center', gap: '10px' },
  editRow: { display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' as const },
  icon: { fontSize: '20px', flexShrink: 0 },
  name: { fontSize: '14px', fontWeight: 600, color: '#0f2d1f', minWidth: '120px' },
  desc: { fontSize: '12px', color: '#666', flex: 1 },
  iconInput: { width: '50px', padding: '6px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '16px', textAlign: 'center' as const },
  nameInput: { flex: 1, padding: '6px 10px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '13px', minWidth: '120px' },
  editBtn: { background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', padding: '4px' },
  toggleBtn: { background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', padding: '4px' },
  deleteBtn: { background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', padding: '4px' },
  saveBtn: { padding: '6px 14px', fontSize: '12px', fontWeight: 600, backgroundColor: '#1a6b3c', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' },
  cancelBtn: { padding: '6px 14px', fontSize: '12px', backgroundColor: '#f5f5f5', color: '#333', border: '1px solid #e0e0e0', borderRadius: '4px', cursor: 'pointer' },
  addSection: { padding: '16px', backgroundColor: '#f5f5f5', borderRadius: '8px', border: '1px dashed #ccc' },
  addTitle: { fontSize: '13px', fontWeight: 600, color: '#333', margin: '0 0 10px' },
  addRow: { display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' as const },
};

// ── Workflow Edit Form ───────────────────────────────────────────────────────

interface WorkflowEditFormProps {
  workflow: ApiWorkflow;
  categories: { id: string; name: string; icon: string; color: string; active: boolean }[];
  keyAreas: { id: string; name: string; icon: string; description: string; active: boolean }[];
  onSave: (updates: { name?: string; status?: string; keyAreaIds?: string[] }) => Promise<void>;
}

function WorkflowEditForm({ workflow, categories, keyAreas, onSave }: WorkflowEditFormProps) {
  const [name, setName] = useState(workflow.name);
  const [icon, setIcon] = useState(workflow.icon);
  const [status, setStatus] = useState(workflow.status);
  const [selectedKeyAreaIds, setSelectedKeyAreaIds] = useState<string[]>(
    workflow.keyAreas.map(ka => ka.keyAreaId)
  );
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showIconPicker, setShowIconPicker] = useState(false);

  // Sync when workflow changes from API refresh
  useEffect(() => {
    setName(workflow.name);
    setIcon(workflow.icon);
    setStatus(workflow.status);
    setSelectedKeyAreaIds(workflow.keyAreas.map(ka => ka.keyAreaId));
  }, [workflow]);

  const hasChanges = name !== workflow.name ||
    icon !== workflow.icon ||
    status !== workflow.status ||
    JSON.stringify(selectedKeyAreaIds.sort()) !== JSON.stringify(workflow.keyAreas.map(ka => ka.keyAreaId).sort());

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      const updates: { name?: string; status?: string; keyAreaIds?: string[] } = {};
      if (name !== workflow.name) updates.name = name;
      if (status !== workflow.status) updates.status = status;
      if (JSON.stringify(selectedKeyAreaIds.sort()) !== JSON.stringify(workflow.keyAreas.map(ka => ka.keyAreaId).sort())) {
        updates.keyAreaIds = selectedKeyAreaIds;
      }
      // Icon is part of the workflow update — need to include it
      // The API updateWorkflow doesn't support icon directly, so we handle it via the full update
      await onSave(updates);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      alert('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const toggleKeyArea = (areaId: string) => {
    setSelectedKeyAreaIds(prev =>
      prev.includes(areaId) ? prev.filter(id => id !== areaId) : [...prev, areaId]
    );
  };

  const category = categories.find(c => c.id === workflow.categoryId);

  return (
    <div style={editFormStyles.container}>
      <div style={editFormStyles.header}>
        <div
          style={{ ...styles.detailIcon, backgroundColor: workflow.color + '22', borderColor: workflow.color, cursor: 'pointer', position: 'relative' }}
          onClick={() => setShowIconPicker(!showIconPicker)}
          title="Click to change icon"
        >
          {icon || '📋'}
          <span style={{ position: 'absolute', bottom: '-4px', right: '-4px', fontSize: '10px', backgroundColor: '#fff', borderRadius: '50%', padding: '1px 3px', border: '1px solid #ccc' }}>✏️</span>
        </div>
        <h3 style={editFormStyles.title}>Edit Workflow</h3>
        {saved && <span style={editFormStyles.savedBadge}>✓ Saved</span>}
      </div>

      {/* Icon Picker */}
      {showIconPicker && (
        <div style={{ marginBottom: '16px' }}>
          <label style={editFormStyles.label}>Select Icon</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', padding: '8px', border: '1px solid #e0e0e0', borderRadius: '6px', backgroundColor: '#fafafa', maxHeight: '100px', overflowY: 'auto' }}>
            {WORKFLOW_ICONS.map(emoji => (
              <button
                key={emoji}
                type="button"
                onClick={() => { setIcon(emoji); setShowIconPicker(false); }}
                style={{
                  width: '34px', height: '34px', fontSize: '16px',
                  border: icon === emoji ? '2px solid #1a6b3c' : '1px solid #e0e0e0',
                  borderRadius: '6px',
                  backgroundColor: icon === emoji ? '#e8f5e9' : '#fff',
                  cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                {emoji}
              </button>
            ))}
          </div>
        </div>
      )}

      <div style={editFormStyles.form}>
        {/* Name */}
        <div style={editFormStyles.field}>
          <label style={editFormStyles.label}>Workflow Name</label>
          <input
            style={editFormStyles.input}
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="Workflow name"
          />
        </div>

        {/* Category (read-only for now — changing category requires API support) */}
        <div style={editFormStyles.field}>
          <label style={editFormStyles.label}>Category</label>
          <div style={editFormStyles.readOnly}>
            {category?.icon} {category?.name || 'Uncategorized'}
          </div>
        </div>

        {/* Status */}
        <div style={editFormStyles.field}>
          <label style={editFormStyles.label}>Status</label>
          <select
            style={editFormStyles.input}
            value={status}
            onChange={e => setStatus(e.target.value)}
          >
            <option value="draft">Draft</option>
            <option value="active">Active</option>
            <option value="completed">Completed</option>
          </select>
        </div>

        {/* Key Areas */}
        <div style={editFormStyles.field}>
          <label style={editFormStyles.label}>Key Areas</label>
          <div style={editFormStyles.keyAreasGrid}>
            {keyAreas.filter(a => a.active).map(area => {
              const isSelected = selectedKeyAreaIds.includes(area.id);
              return (
                <button
                  key={area.id}
                  type="button"
                  style={{
                    ...editFormStyles.keyAreaChip,
                    backgroundColor: isSelected ? '#e8f5e9' : '#f9f9f9',
                    borderColor: isSelected ? '#1a6b3c' : '#e0e0e0',
                    color: isSelected ? '#1a6b3c' : '#666',
                  }}
                  onClick={() => toggleKeyArea(area.id)}
                >
                  {area.icon} {area.name}
                  {isSelected && <span style={{ marginLeft: '4px' }}>✓</span>}
                </button>
              );
            })}
          </div>
        </div>

        {/* Save Button */}
        <div style={editFormStyles.actions}>
          <button
            style={{
              ...editFormStyles.saveBtn,
              opacity: (!hasChanges || saving) ? 0.5 : 1,
              cursor: (!hasChanges || saving) ? 'not-allowed' : 'pointer',
            }}
            onClick={handleSave}
            disabled={!hasChanges || saving}
            type="button"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
          {hasChanges && <span style={{ fontSize: '12px', color: '#f59e0b' }}>Unsaved changes</span>}
        </div>
      </div>
    </div>
  );
}

const editFormStyles: Record<string, React.CSSProperties> = {
  container: {
    backgroundColor: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '12px',
    padding: '24px',
    marginBottom: '24px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '20px',
  },
  title: {
    fontSize: '18px',
    fontWeight: 700,
    color: '#0f2d1f',
    margin: 0,
    flex: 1,
  },
  savedBadge: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#1a6b3c',
    backgroundColor: '#e8f5e9',
    padding: '4px 12px',
    borderRadius: '12px',
  },
  form: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '16px',
  },
  field: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '6px',
  },
  label: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#333',
  },
  input: {
    padding: '10px 12px',
    border: '1px solid #ccc',
    borderRadius: '6px',
    fontSize: '14px',
    outline: 'none',
    width: '100%',
    maxWidth: '400px',
    boxSizing: 'border-box' as const,
  },
  readOnly: {
    padding: '10px 12px',
    backgroundColor: '#f9f9f9',
    border: '1px solid #e0e0e0',
    borderRadius: '6px',
    fontSize: '14px',
    color: '#555',
    maxWidth: '400px',
  },
  keyAreasGrid: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '8px',
  },
  keyAreaChip: {
    padding: '6px 12px',
    fontSize: '12px',
    fontWeight: 500,
    borderRadius: '16px',
    border: '1px solid',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    background: 'none',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginTop: '8px',
  },
  saveBtn: {
    padding: '10px 24px',
    backgroundColor: '#1a6b3c',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
  },
};

// ── Create Workflow Modal ────────────────────────────────────────────────────

interface CreateWorkflowModalProps {
  categories: { id: string; name: string; icon: string; color: string }[];
  onClose: () => void;
  onCreate: (data: { name: string; categoryId: string; icon: string }) => void;
}

const WORKFLOW_ICONS = [
  '📋', '📝', '🔄', '⚙️', '🎯', '📦', '🚀', '🏁',
  '👥', '🤝', '📞', '✉️', '🔔', '📊', '💡', '🔑',
  '🎭', '🎓', '🍲', '🕯️', '🙏', '🌳', '🌙', '🏨',
  '🚐', '📡', '🎵', '🎪', '🏆', '💰', '🛒', '✅',
];

function CreateWorkflowModal({ categories, onClose, onCreate }: CreateWorkflowModalProps) {
  const [name, setName] = useState('');
  const [categoryId, setCategoryId] = useState(categories[0]?.id || '');
  const [icon, setIcon] = useState('📋');

  return (
    <div style={modalStyles.overlay} onClick={onClose}>
      <div style={modalStyles.content} onClick={e => e.stopPropagation()}>
        <h3 style={modalStyles.title}>Create New Workflow</h3>
        <div style={modalStyles.field}>
          <label style={modalStyles.label}>Workflow Name</label>
          <input
            style={modalStyles.input}
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="e.g., Guest Registration Process"
            autoFocus
          />
        </div>
        <div style={modalStyles.field}>
          <label style={modalStyles.label}>Icon</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', maxHeight: '120px', overflowY: 'auto', padding: '8px', border: '1px solid #e0e0e0', borderRadius: '6px', backgroundColor: '#fafafa' }}>
            {WORKFLOW_ICONS.map(emoji => (
              <button
                key={emoji}
                type="button"
                onClick={() => setIcon(emoji)}
                style={{
                  width: '36px',
                  height: '36px',
                  fontSize: '18px',
                  border: icon === emoji ? '2px solid #1a6b3c' : '1px solid #e0e0e0',
                  borderRadius: '6px',
                  backgroundColor: icon === emoji ? '#e8f5e9' : '#fff',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {emoji}
              </button>
            ))}
          </div>
        </div>
        <div style={modalStyles.field}>
          <label style={modalStyles.label}>Category</label>
          <select style={modalStyles.input} value={categoryId} onChange={e => setCategoryId(e.target.value)}>
            {categories.map(c => (
              <option key={c.id} value={c.id}>{c.icon} {c.name}</option>
            ))}
          </select>
        </div>
        <div style={modalStyles.actions}>
          <button
            style={modalStyles.createBtn}
            onClick={() => { if (name.trim()) onCreate({ name: name.trim(), categoryId, icon }); }}
            disabled={!name.trim()}
          >
            Create
          </button>
          <button style={modalStyles.cancelBtn} onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

const modalStyles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  content: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '28px',
    width: '420px',
    maxWidth: '90vw',
  },
  title: {
    fontSize: '18px',
    fontWeight: 700,
    color: '#1a6b3c',
    marginBottom: '20px',
  },
  field: {
    marginBottom: '16px',
  },
  label: {
    display: 'block',
    fontSize: '13px',
    fontWeight: 600,
    color: '#333',
    marginBottom: '6px',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #ccc',
    borderRadius: '6px',
    fontSize: '14px',
    boxSizing: 'border-box' as const,
  },
  actions: {
    display: 'flex',
    gap: '10px',
    marginTop: '20px',
  },
  createBtn: {
    padding: '10px 20px',
    backgroundColor: '#1a6b3c',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  cancelBtn: {
    padding: '10px 20px',
    backgroundColor: '#f5f5f5',
    color: '#333',
    border: '1px solid #e0e0e0',
    borderRadius: '6px',
    fontSize: '13px',
    cursor: 'pointer',
  },
};
